import { cn } from '@commenda/utils';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cva, type VariantProps } from 'class-variance-authority';
import { Circle } from 'lucide-react';
import React from 'react';

const radioGroupVariants = cva('flex gap-2', {
  variants: {
    size: {
      sm: 'gap-2',
      lg: 'gap-3',
    },
  },
  defaultVariants: {
    size: 'sm',
  },
});

const radioGroupItemVariants = cva(
  'shrink-0 rounded-full border border-border-primary data-[state=checked]:border-fg-brand data-[state=checked]:bg-fg-brand data-[state=checked]:hover:bg-fg-brand-hover data-[state=checked]:hover:border-fg-brand-hover transition-all disabled:cursor-not-allowed disabled:border-border-disabled disabled:data-[state=checked]:border-border-disabled disabled:bg-bg-disabled-subtle disabled:data-[state=checked]:bg-bg-disabled-subtle disabled:data-[state=checked]:text-fg-disabled focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-fg-brand',
  {
    variants: {
      size: {
        sm: 'h-4 w-4',
        lg: 'h-5 w-5',
      },
    },
    defaultVariants: {
      size: 'sm',
    },
  }
);

interface RadioGroupProps
  extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>,
    VariantProps<typeof radioGroupVariants> {}

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  RadioGroupProps
>(({ className, size, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root
      className={cn(radioGroupVariants({ size, className }))}
      {...props}
      ref={ref}
    />
  );
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

interface RadioGroupItemProps
  extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>,
    VariantProps<typeof radioGroupItemVariants> {}

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps
>(({ className, size, disabled, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(radioGroupItemVariants({ size, className }))}
      disabled={disabled}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex h-full w-full items-center justify-center">
        <Circle
          className={cn(
            size === 'sm' ? 'h-1.5 w-1.5' : 'h-2 w-2',
            disabled
              ? 'fill-fg-disabled text-fg-disabled'
              : 'fill-fg-white text-fg-white'
          )}
        />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

interface RadioGroupCardItemProps extends RadioGroupItemProps {
  children: React.ReactNode;
  cardClassName?: string;
}

const RadioGroupCardItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupCardItemProps
>(({ children, cardClassName, size, disabled, className, ...props }, ref) => {
  return (
    <label
      className={cn(
        'flex items-center gap-4 p-4 border border-border-primary rounded-lg cursor-pointer transition-colors bg-bg-primary',
        'has-[[data-state=checked]]:border-fg-brand has-[[data-state=checked]]:bg-bg-brand-secondary',
        'hover:border-border-primary-hover',
        disabled && 'cursor-not-allowed opacity-50',
        cardClassName
      )}
    >
      <RadioGroupItem
        ref={ref}
        size={size}
        disabled={disabled}
        className={className}
        {...props}
      />
      {children}
    </label>
  );
});
RadioGroupCardItem.displayName = 'RadioGroupCardItem';

export { RadioGroup, RadioGroupCardItem, RadioGroupItem };
